import type { CSSProperties, ReactNode } from "react"
import { Head } from "@inertiajs/react"
import { AppSidebar } from "@/components/app-sidebar"
import { MobileTopBar } from "@/components/mobile-top-bar"
import { SiteHeader } from "@/components/site-header"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { useIsMobileShell } from "@/hooks/use-is-mobile-shell"

interface AppShellProps {
  title: string
  headTitle?: string
  backTo?: string
  action?: ReactNode
  children: ReactNode
}

const sidebarStyle = {
  "--sidebar-width": "calc(var(--spacing) * 72)",
  "--header-height": "calc(var(--spacing) * 12)",
} as CSSProperties

export function AppShell({ title, headTitle, backTo, action, children }: AppShellProps) {
  const isMobile = useIsMobileShell()

  if (isMobile) {
    return (
      <>
        <Head title={headTitle ?? title} />
        <MobileTopBar title={title} backTo={backTo} action={action} />
        <main className="flex flex-1 flex-col">
          <div className="flex flex-col gap-3 py-3">{children}</div>
        </main>
      </>
    )
  }

  // Desktop: the sidebar's existing quick-create slot (e.g. NavMain's
  // "Add Device" button) covers the same intent as `action`, so the
  // SiteHeader doesn't render it — avoids duplicate buttons on desktop.
  return (
    <SidebarProvider style={sidebarStyle}>
      <Head title={headTitle ?? title} />
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title={title} />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">{children}</div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
